import { gsap } from 'gsap';
import type { PlaybackOwner } from '@/types/canvas-type';

export class Timeline {
  private _duration: number;
  private timeline: gsap.core.Timeline | null = null;
  private emptyTween: gsap.core.Tween | null = null;
  private playbackLimitTween: gsap.core.Tween | null = null;
  private playbackLimitHandler: (() => void) | null = null;
  private playbackOwner: PlaybackOwner = 'idle';

  public onStart?: () => void;
  public onUpdate?: (progress: number) => void;
  public onComplete?: () => void;

  constructor(duration: number = 20) {
    this._duration = duration;
  }

  public init() {
    this.timeline = gsap.timeline({
      paused: true,
      onStart: () => {
        if (this.onStart) this.onStart();
      },
      onUpdate: () => {
        if (this.onUpdate) this.onUpdate(this.timeline!.progress());
      },
      onComplete: () => {
        this.playbackOwner = 'idle';
        if (this.onComplete) this.onComplete();
      }
    });

    this.initEmptyTween();
  }

  /**
   * emptyTween 只负责保证时间轴有最低时长，不再承担回调职责
   */
  private initEmptyTween() {
    if (this.emptyTween) {
      this.emptyTween.kill();
      this.emptyTween = null;
    }

    this.emptyTween = gsap.to({ x: 0 }, { x: 0, duration: this._duration });
    this.timeline?.add(this.emptyTween, 0);
  }

  public play() {
    if (!this.timeline) return;

    this.clearPlaybackLimit();
    if (this.timeline.progress() === 1) this.timeline.progress(0);

    this.playbackOwner = 'main';
    this.timeline.play();
  }

  public pause() {
    this.timeline?.pause();
    this.clearPlaybackLimit();
    this.playbackOwner = 'idle';
  }

  /** 精确播放到指定时刻，不向主时间线写入 pause 节点。 */
  public playUntil(time: number, handler: () => void): void {
    if (!this.timeline) return;

    // 辅助 tweenTo 与主时间线互斥，禁止两个播放驱动同时推进 playhead。
    this.timeline.pause();
    this.clearPlaybackLimit();
    const normalizedTime = Math.max(0, Math.min(time, this._duration));
    this.playbackLimitHandler = handler;
    this.playbackOwner = 'page';
    this.playbackLimitTween = this.timeline.tweenTo(normalizedTime, {
      ease: 'none',
      onComplete: this.handlePlaybackLimit
    });
  }

  public clearPlaybackLimit(): void {
    const limitTween = this.playbackLimitTween;
    this.playbackLimitTween = null;
    this.playbackLimitHandler = null;
    limitTween?.kill();
    if (this.playbackOwner === 'page') this.playbackOwner = 'idle';
  }

  private readonly handlePlaybackLimit = () => {
    const handler = this.playbackLimitHandler;
    this.playbackLimitTween = null;
    this.playbackLimitHandler = null;
    this.playbackOwner = 'idle';
    handler?.();
  };

  public seek(time: number) {
    this.timeline?.pause();
    this.clearPlaybackLimit();
    this.playbackOwner = 'idle';
    this.timeline?.time(time);
    //使用seek时，第二个参数为false，才能触发onUpdate方法
    // this.timeline?.seek(time, false)
  }

  /**
   * 强制 seek：即使时间没变也会重新渲染
   * 用于 tween 结构重建后需要刷新的场景
   */
  public forceSeek(time: number) {
    if (!this.timeline) return;
    this.timeline.pause();
    this.clearPlaybackLimit();
    this.playbackOwner = 'idle';
    // 2. 渲染到目标时间，此时 GSAP 能正确检测状态跨越，
    //    触发 onStart / onReverseComplete / onUpdate 等回调
    this.timeline.render(this._duration, false, true);
    this.timeline.render(time, false, true);
  }

  /**
   * @param skipRender 批量添加时设为 true，由调用方统一调用 forceSeek() 避免重复渲染
   */
  public addTween(
    tween: gsap.core.Timeline | gsap.core.Tween,
    startTime: number,
    skipRender = false
  ) {
    this.timeline?.add(tween, startTime);

    if (!skipRender) {
      // 强制重渲染当前时间，以正确触发 onStart/onReverseComplete 回调
      const currentTime = this.timeline?.time() ?? 0;
      this.timeline?.time(currentTime);
    }
  }

  public removeTween(tween: gsap.core.Timeline | gsap.core.Tween) {
    if (!tween || !this.timeline) return;

    // 直接尝试移除
    this.timeline.remove(tween);
  }

  /**
   * 重置 timeline：清空所有子动画/标签/回调，但不销毁 timeline 实例
   * 重置后可以继续 addTween 添加新内容
   */
  public reset() {
    if (!this.timeline) return;

    this.clearPlaybackLimit();

    // 清除 emptyTween 引用（clear 会移除子动画，但需要先 kill 掉旧的 tween 实例）
    this.emptyTween?.kill();
    this.emptyTween = null;

    // 清空所有子动画、标签、回调，但不销毁 timeline
    this.timeline.clear();
    // 暂停并播放头归零，防止重置时时间轴正在播放导致意外继续运行
    this.timeline.pause().seek(0);
    this.playbackOwner = 'idle';

    // 重新添加 emptyTween 作为时间轴驱动
    this.initEmptyTween();
  }

  public destroy() {
    if (!this.timeline) return;
    this.clearPlaybackLimit();
    this.emptyTween?.kill();

    this.timeline.clear();
    this.timeline.kill();
    this.timeline = null;
    this.playbackOwner = 'idle';
  }

  public get playing(): boolean {
    if (this.playbackOwner === 'page') return this.playbackLimitTween !== null;
    if (this.playbackOwner === 'main') return this.timeline ? !this.timeline.paused() : false;
    return false;
  }

  public get owner(): PlaybackOwner {
    return this.playbackOwner;
  }

  public get duration() {
    return this._duration;
  }

  public set duration(v: number) {
    this._duration = v;
    if (this.timeline) {
      this.initEmptyTween();
    }
  }
}
